import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useAccountPref } from '../lib/tableCols';
import { useFitHeight } from '../lib/fitHeight';
import { usePhone } from '../lib/phone';
import { Link } from 'react-router-dom';
import { api, type MoGroup, type MoLine } from '../lib/api';
import { ErrorBox, dash, money, moneyInput, type Row, IdCell, MoneyInput, PAGE_ALL, usePageSize } from '../components/DataTable';
import ColumnFilter from '../components/ColumnFilter';
import { activeFilterCount, distinctCounts, passes, type ColFilters } from '../lib/colFilter';
import { accountTotals, filterGroups, idsToSend, keyOf, MO_GETTERS, pickable, pruneTo, selectableIds, setAll, sortGroups, type MoSort, type Side } from '../lib/moBoard';
import { canScreen, useMe } from '../lib/me';
import DriveMonPicker from '../components/DriveMonPicker';
import { pickDriveMon, useStickyDriveMon } from '../lib/driveMon';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';
import { useUrlSearch } from '../lib/deepLink';
import { AccountField, BulkPanel, DefForm, TargetPicker, type Target } from './ResultMoDef';
import { AllocChangeBanner } from '../components/AllocChangeAlerts';
import { CalcNote } from '../components/CalcNote';

/**
 * 월정기 — 매월 반복되는 기사 지급·공제(레거시 `ResultMo`).
 *
 * **좌우 한 화면**(2026-09-30 way — 레거시 「월정기 정산등록」): 왼쪽 정기항목(`result_mo_def`) → 오른쪽 그 달 월정산(`result_mo`).
 * 양쪽 다 차주별 묶음·소계, 페이지는 차주 단위(`listResultMoBoard`). 가운데 전체등록·선택등록·선택취소·전체취소.
 * 정기항목 자체를 고치는 건 `/result-mo-def`.
 *
 * ⚠️ 등록은 **그 달에 이미 있으면 건너뛴다**(레거시와 동일). 전체등록을 두 번 눌러도 중복되지 않는다.
 * ⚠️ 취소는 그 달 항목만 내린다 — **템플릿은 그대로다.** 다음 달에 다시 등록할 수 있다.
 * ⚠️ 마감 축이 기사정산과 **다르다**: 월정기는 `월정기정산` 마감을 본다.
 * ⚠️ **금액 0 템플릿(휴면)은 등록되지 않는다.** 레거시 목록 SP 가 0원 행을 숨겨 고를 수 없던 것과 같은 결과다.
 */

/** 그 달 항목의 금액·계정 수정. 템플릿은 안 건드린다 — 이번 달만 다른 경우가 흔하다. */
function MonthEditor({
  row, driveMon, onClose, onSaved,
}: { row: Row; driveMon: string; onClose: () => void; onSaved: (msg: string) => void }) {
  const [accountName, setAccountName] = useState(String(row.account_name ?? ''));
  const [amt, setAmt] = useState(moneyInput(row.amt));
  const [comment, setComment] = useState(String(row.comment ?? ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true); setError(null);
    try {
      await api.saveResultMo({
        resultMoId: Number(row.result_mo_id), driveMon,
        accountName, amt: amt.trim() === '' ? '0' : amt, comment: comment || undefined,
      });
      onSaved('이 달 항목을 수정했습니다.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="btn-row-sm flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">{driveMon} 항목 수정</h2>
        <span className="text-sm text-ink-soft">
          {dash(row.driver_name)} · {dash(row.car_no)} · {dash(row.owner_name)}
        </span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? '저장 중…' : '수정'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <p className="rounded bg-canvas px-3 py-2 text-xs text-ink-soft">
        이 달 값만 바뀝니다. 정기항목(템플릿)은 그대로라 다음 달에는 다시 <b>{money(row.def_amt)}</b> 로 등록됩니다 —
        매달 이 값이어야 한다면 <Link className="underline" to="/result-mo-def">정기항목</Link>에서 고치세요.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">계정</span>
          <AccountField isPlus={row.is_plus === true} value={accountName} onChange={setAccountName} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">금액</span>
          <MoneyInput value={amt} onChange={setAmt} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">비고</span>
          <input className="field" value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
      </div>
    </div>
  );
}

/**
 * 편성변경 — 항목의 대상(기사·차량·차주·소속)을 편성에서 골라 바로 옮긴다(2026-09-28 way).
 * 정기항목(다음 달부터)과 **보고 있는 달에 등록된 줄**을 한 번에 옮긴다 — 한쪽만 옮기면
 * 화면(정기항목 기준)과 급여명세로 가는 그 달 줄이 서로 다른 기사를 가리킨다.
 */
function ReassignEditor({
  rows, driveMon, finished, onClose, onSaved,
}: { rows: Row[]; driveMon: string; finished: boolean; onClose: () => void; onSaved: (msg: string) => void }) {
  const [target, setTarget] = useState<Target | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const one = rows.length === 1 ? rows[0]! : null;
  const appliedCount = rows.filter((r) => r.result_mo_id).length;

  async function save() {
    if (!target) return;
    setSaving(true); setError(null);
    try {
      const r = await api.reassignResultMo({
        driveMon, defIds: rows.map((x) => Number(x.result_mo_def_id)),
        driverId: target.driverId, carId: target.carId, ownerId: target.ownerId, grpId: target.grpId,
      });
      onSaved(
        `정기항목 ${r.defsUpdated.toLocaleString('ko-KR')}건을 ${target.label} 로 옮겼습니다`
        + (r.monthUpdated > 0 ? ` · ${driveMon} 등록분 ${r.monthUpdated.toLocaleString('ko-KR')}건도 함께` : '')
        + (r.monthSkippedFinished ? ` · ${driveMon} 은 월정산 마감이라 등록분은 그대로입니다` : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">편성변경</h2>
        <span className="text-sm text-ink-soft">
          {one
            ? <>{dash(one.def_account_name)} · 지금 {dash(one.driver_name)} · {dash(one.car_no)} · {dash(one.owner_name)} · {dash(one.grp_name)}</>
            : <>고른 항목 {rows.length.toLocaleString('ko-KR')}건</>}
        </span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>닫기</button>
        <button className="btn-primary" onClick={() => void save()} disabled={saving || !target}>
          {saving ? '옮기는 중…' : '이 편성으로 바꾸기'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <p className="rounded bg-canvas px-3 py-2 text-xs text-ink-soft">
        정기항목이 새 편성으로 바뀌어 <b>다음에 등록하는 달부터</b> 새 기사로 나갑니다.
        {appliedCount > 0 && (finished
          ? <> {driveMon} 은 월정산 마감이라 <b>이미 등록된 {appliedCount}건은 그대로</b>입니다.</>
          : <> <b>{driveMon} 에 이미 등록된 {appliedCount}건도 함께</b> 옮깁니다.</>)}
        {' '}지난달 명세는 바뀌지 않습니다.
      </p>

      <TargetPicker onPick={setTarget} />
      {target && (
        <div className="rounded-lg border border-brand-100 bg-brand-50 px-3 py-2 text-sm">
          바꿀 편성: <b>{target.label}</b>
        </div>
      )}
    </div>
  );
}

const OWNERS_PER_PAGE = 30;

type Board = {
  owners: number; n: number; plus: string; minus: string; isFinished: boolean; groups: MoGroup[];
  /** 왼쪽만 — 편성이 전부 그 달 전에 끝난 정기항목 수(목록 기본·전체등록에서 빠진다 · 10-30). */
  endedCount?: number;
};

/** 서버 한 번에 받는 차주 수(서버 상한 100). */
const FETCH_OWNERS = 100;

/**
 * 한 쪽(왼쪽 정기항목·오른쪽 월정산) 목록 — **그 달 전부**를 받는다(2026-10-02 함남열).
 * 칼럼 거르기·머리 전체 고르기·엑셀이 「지금 보이는 쪽」이 아니라 「검색에 걸린 전부」를 봐야 해서다.
 * 2026-10 실측 차주 260 · 3,746줄 · 1.2MB · 0.3~0.5초(차주 100명씩 3번, 둘째부터 동시에 —
 * `functions/scripts/result-mo-board-size.mjs`).
 */
function useBoard(side: Side, mon: string, q: string, pendingOnly: boolean, nonce: number, ended = false) {
  const [data, setData] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!mon) return;
    let alive = true;
    setLoading(true); setError(null);
    const page = (offset: number) => api.listResultMoBoard({
      driveMon: mon, side, q: q || undefined, pendingOnly, ended: ended || undefined, limit: FETCH_OWNERS, offset,
    });
    (async () => {
      const first = await page(0);
      const rest: Promise<Board>[] = [];
      for (let off = FETCH_OWNERS; off < first.owners; off += FETCH_OWNERS) rest.push(page(off));
      const more = await Promise.all(rest);
      return { ...first, groups: [first, ...more].flatMap((p) => p.groups) };
    })()
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [side, mon, q, pendingOnly, nonce, ended]);
  return { data, loading, error };
}

const plusOf = (r: MoLine) => (r.is_plus ? r.amt : null);
const minusOf = (r: MoLine) => (r.is_plus ? null : r.amt);

/** 표 칸 하나 — 머리글·거르기·칸 폭·폰 처리를 한 곳에서. 칸 이름(`key`)은 `MO_GETTERS` 와 같다. */
interface MoCol {
  key: string; label: string; w: number; right?: boolean;
  /** 폰에서 뺀다 · 폰에서 왼쪽 고정(2026-09-30 모바일 4차와 같은 칸). */
  phoneHide?: boolean; pin?: boolean;
  cell: (r: MoLine) => ReactNode;
  title?: (r: MoLine) => string | undefined;
}

function moCols(side: Side): MoCol[] {
  const cols: MoCol[] = [];
  if (side === 'month') {
    cols.push({
      key: 'no', label: 'NO', w: 92, right: true, phoneHide: true,
      cell: (r) => (
        <>
          {r.result_mo_id}
          {r.result_mo_def_id == null && (
            <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800" title="정기항목에서 오지 않은 수동 등록분">수동</span>
          )}
        </>
      ),
    });
  }
  cols.push(
    { key: 'driver', label: '기사', w: 104, pin: true, cell: (r) => dash(r.driver_name), title: (r) => r.driver_name ?? undefined },
    // 차주(10-14 #246 홍명옥 — 「기사명·차주명을 함께」 · 차량팀 협의) · 거르기는 위 「차주 ▾」와 같은 값을 공유한다.
    { key: 'owner', label: '차주', w: 104, cell: (r) => dash(r.owner_name), title: (r) => r.owner_name ?? undefined },
    { key: 'car', label: '차량', w: 112, cell: (r) => dash(r.car_no), title: (r) => r.car_no ?? undefined },
    { key: 'grp', label: '소속', w: 92, phoneHide: true, cell: (r) => dash(r.grp_name), title: (r) => r.grp_name ?? undefined },
    {
      key: 'account', label: '계정', w: 132, title: (r) => r.account_name ?? undefined,
      cell: (r) => (
        <>
          {dash(r.account_name)}
          {side === 'def' && r.result_mo_id != null && (
            <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] text-emerald-700">등록됨</span>
          )}
        </>
      ),
    },
    { key: 'plus', label: '지급', w: 84, right: true, cell: (r) => (plusOf(r) ? money(plusOf(r)) : '') },
    { key: 'minus', label: '공제', w: 84, right: true, cell: (r) => (minusOf(r) ? money(minusOf(r)) : '') },
    { key: 'alloc', label: '편성 ID', w: 66, right: true, phoneHide: true, cell: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'driver_id', label: '기사 ID', w: 66, right: true, phoneHide: true, cell: (r) => <IdCell v={r.driver_id} /> },
    { key: 'owner_id', label: '차주 ID', w: 66, right: true, phoneHide: true, cell: (r) => <IdCell v={r.owner_id} /> },
    {
      key: 'comment', label: '비고', w: 150, title: (r) => r.comment ?? undefined,
      cell: (r) => {
        const differs = side === 'month' && r.def_amt != null && Number(r.amt) !== Number(r.def_amt);
        return (
          <>
            {differs && <span className="mr-1 text-amber-700" title={`정기항목 ${money(r.def_amt)}`}>금액수정됨</span>}
            {r.comment ?? ''}
          </>
        );
      },
    },
    { key: 'created', label: '등록일', w: 84, phoneHide: true, cell: (r) => dash(r.created_at) },
    // 버스인입력담당자(11-03 way · 재무추가와 같은 칸) — 정기항목은 만든 사람, 월정산은 그 달 줄을 등록한 사람.
    { key: 'creator', label: '버스인입력담당자', w: 104, phoneHide: true, cell: (r) => dash(r.created_name), title: (r) => r.created_name ?? undefined },
  );
  return cols;
}

/**
 * 칸 순서(#123 한정웅 — 구 버스인처럼 사용자가 바꾼다) — 머리글을 끌어 옮긴다. 양쪽(정기항목·월정산)이 **같은 순서**를 쓰고
 * 이 브라우저에 기억한다. 저장은 월정산 쪽 칸 전부(NO 포함)의 순서라 한쪽에만 있는 칸도 자리를 잃지 않는다.
 * 폰은 기본 순서(왼쪽 고정 칸이 맨 앞이어야 한다).
 */
const COL_ORDER_KEY = 'result-mo.colOrder';
const ALL_COL_KEYS = moCols('month').map((c) => c.key);

/** 칸 순서 — 계정에 기억(10-15 way 「항목저장 계정별」 · 키 `result-mo.colorder`, 브라우저엔 사본). */
function useColOrder(): [string[] | null, (o: string[] | null) => void] {
  return useAccountPref<string[]>('result-mo.colorder', COL_ORDER_KEY,
    (v) => (Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : null));
}

/**
 * 칸 폭(#257) — 머리글 오른쪽 끝을 끌어 바꾼다. 양쪽 표가 같이 따르고 이 브라우저에 기억한다(칸 순서와 같은 방식).
 * 저장 = 바꾼 칸만 {키: px}. 폰은 기본 폭.
 */
const COL_WIDTH_KEY = 'result-mo.colWidth';
type ColWidths = Record<string, number>;

/** 칸 폭 — 계정에 기억(키 `result-mo.colwidth`, 브라우저엔 사본). */
function useColWidths(): [ColWidths | null, (w: ColWidths | null) => void] {
  const [widths, setRaw] = useAccountPref<ColWidths>('result-mo.colwidth', COL_WIDTH_KEY, (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    const out: ColWidths = {};
    for (const [k, n] of Object.entries(v)) if (typeof n === 'number' && n > 0) out[k] = n;
    return Object.keys(out).length ? out : null;
  });
  const set = (w: ColWidths | null) => setRaw(w && Object.keys(w).length ? w : null);
  return [widths, set];
}

const COL_MIN_W = 40;
const COL_MAX_W = 600;

/** 저장된 순서로 칸 키 전부를 늘어놓는다 — 저장에 없는 칸(나중에 생긴 칸)은 기본 자리 순서대로 끝에. */
function fullOrder(order: string[] | null): string[] {
  const known = (order ?? []).filter((k, i, a) => ALL_COL_KEYS.includes(k) && a.indexOf(k) === i);
  // 저장한 순서에 없는 새 칸(예: #246 차주)은 기본 순서상 바로 앞 칸 뒤에 끼운다 — 맨 끝으로 밀리지 않게.
  ALL_COL_KEYS.forEach((k, i) => {
    if (known.includes(k)) return;
    const prev = i > 0 ? known.indexOf(ALL_COL_KEYS[i - 1]!) : -1;
    known.splice(prev + 1, 0, k);
  });
  return known;
}

function applyOrder(cols: MoCol[], order: string[] | null): MoCol[] {
  if (!order) return cols;
  const rank = new Map(fullOrder(order).map((k, i) => [k, i]));
  return [...cols].sort((a, b) => (rank.get(a.key) ?? 0) - (rank.get(b.key) ?? 0));
}

/** `from` 칸을 `to` 칸 자리로 — 오른쪽으로 끌면 그 칸 뒤, 왼쪽으로 끌면 그 칸 앞. 기본 순서와 같아지면 null(저장 지움). */
function moveCol(order: string[] | null, from: string, to: string): string[] | null {
  const keys = fullOrder(order);
  const fi = keys.indexOf(from); const ti = keys.indexOf(to);
  if (fi < 0 || ti < 0 || fi === ti) return order;
  keys.splice(fi, 1);
  keys.splice(ti, 0, from);
  return keys.join() === ALL_COL_KEYS.join() ? null : keys;
}

/** 거르기에 쓸 값 목록 — 그 칸을 뺀 나머지 거르기를 통과한 줄에서 센다(엑셀과 같다). */
function valuesFor(rows: MoLine[], filters: ColFilters, key: string) {
  const get = MO_GETTERS[key]!;
  return distinctCounts(rows.filter((r) => passes(r, filters, MO_GETTERS, key)), get);
}

/** 줄 높이 — 보이는 부분만 그리기(가상 스크롤)의 기준. 실제 높이를 재서 맞춘다. */
const ROW_H = 30;
const OVERSCAN = 12;

type Item = { g: MoGroup; r?: MoLine };

/**
 * 차주 묶음 표 — 머리줄(차주·건수·소계) + 줄들. 왼쪽·오른쪽이 같은 모양을 쓴다.
 * **보이는 줄만 그린다**(전체 보기 3,700줄+차주 260줄 — 다 그리면 체크 한 번에도 표 전체를 다시 배치한다).
 * 그래서 줄 높이를 고정한다(칸은 한 줄·말줄임, 버튼은 낮게 · 칸 폭은 고정 — 스크롤하며 폭이 출렁이지 않게).
 */
function Pane({
  side, groups, allRows, loading, empty, picked, setPicked, selectable, filters, setFilters, resetKey,
  canEdit, onEdit, onReassign, onDelete, colOrder, setColOrder, colWidths, setColWidths, sort, setSort,
}: {
  side: Side; groups: MoGroup[]; allRows: MoLine[]; loading: boolean; empty: string;
  colOrder: string[] | null; setColOrder: (o: string[] | null) => void;
  colWidths: ColWidths | null; setColWidths: (w: ColWidths | null) => void;
  /** 칸 정렬(#411) — 머리글 이름을 누르면 오름 → 내림 → 원래 순서. */
  sort: MoSort; setSort: (s: MoSort) => void;
  picked: Set<number>; setPicked: (s: Set<number>) => void;
  /** 지금 검색·거르기에 걸린 줄 중 고를 수 있는 번호 전부(모든 쪽) — 머리 전체 고르기가 이것을 고른다. */
  selectable: number[];
  filters: ColFilters; setFilters: (f: ColFilters) => void;
  /** 바뀌면 맨 위로(쪽·거르기·검색이 바뀔 때). */
  resetKey: string;
  canEdit: boolean;
  /** 오른쪽 = 그 달 금액수정 · 왼쪽 = 정기항목 수정(설정값) */
  onEdit: (r: MoLine) => void; onReassign: (r: MoLine) => void;
  /** 왼쪽 정기항목 삭제 */
  onDelete?: (r: MoLine) => void;
}) {
  const phone = usePhone();
  // 끄는 동안의 폭(놓을 때 저장) — 움직일 때마다 저장하지 않게.
  const [liveW, setLiveW] = useState<{ key: string; w: number } | null>(null);
  const cols = useMemo(() => {
    if (phone) return moCols(side).filter((c) => !c.phoneHide);
    return applyOrder(moCols(side), colOrder).map((c) => {
      const w = liveW?.key === c.key ? liveW.w : colWidths?.[c.key];
      return w ? { ...c, w } : c;
    });
  }, [side, phone, colOrder, colWidths, liveW]);
  const resizing = useRef(false);
  const startResize = (e: ReactPointerEvent, c: MoCol) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = true;
    const x0 = e.clientX; const w0 = c.w;
    let w = w0;
    const move = (ev: PointerEvent) => {
      w = Math.min(COL_MAX_W, Math.max(COL_MIN_W, Math.round(w0 + ev.clientX - x0)));
      setLiveW({ key: c.key, w });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      resizing.current = false;
      setLiveW(null);
      if (w !== w0) setColWidths({ ...(colWidths ?? {}), [c.key]: w });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const dragKey = useRef<string | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const items = useMemo<Item[]>(() => groups.flatMap((g) => [{ g }, ...g.rows.map((r) => ({ g, r }))]), [groups]);

  const scroller = useRef<HTMLDivElement>(null);
  const paneH = useFitHeight(scroller);
  const [vp, setVp] = useState({ top: 0, h: 600 });
  const [rowH, setRowH] = useState(ROW_H);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const sync = () => setVp({ top: el.scrollTop, h: el.clientHeight });
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
    setVp((v) => ({ ...v, top: 0 }));
  }, [resetKey]);
  // 줄 높이를 실제로 재서 맞춘다(글꼴·확대에 따라 30px 이 아닐 수 있다 — 어긋나면 스크롤 끝에서 줄이 빈다).
  useLayoutEffect(() => {
    const tr = scroller.current?.querySelector<HTMLTableRowElement>('tr[data-item]');
    if (tr && tr.offsetHeight > 0 && tr.offsetHeight !== rowH) setRowH(tr.offsetHeight);
  });

  const start = Math.max(0, Math.floor(vp.top / rowH) - OVERSCAN);
  const end = Math.min(items.length, Math.ceil((vp.top + vp.h) / rowH) + OVERSCAN);

  const pickedIn = useMemo(() => selectable.filter((id) => picked.has(id)).length, [selectable, picked]);
  const allOn = selectable.length > 0 && pickedIn === selectable.length;
  const headBox = useRef<HTMLInputElement>(null);
  useEffect(() => { if (headBox.current) headBox.current.indeterminate = pickedIn > 0 && !allOn; }, [pickedIn, allOn]);

  const toggle = (ids: number[], on: boolean) => setPicked(setAll(picked, ids, on));
  const setFilter = (key: string, sel: string[] | undefined) => setFilters({ ...filters, [key]: sel });

  const actW = side === 'def' ? 150 : 76;
  const totalW = (canEdit ? 28 + actW : 0) + cols.reduce((s, c) => s + c.w, 0);
  const ncol = cols.length + (canEdit ? 2 : 0);
  const td ='overflow-hidden text-ellipsis whitespace-nowrap px-2';
  const th = 'sticky top-0 z-10 overflow-hidden whitespace-nowrap border-b border-line bg-surface px-2 py-1.5 text-left font-medium text-ink-soft';
  const pinTd = 'sticky left-0 z-[5] bg-inherit shadow-[1px_0_0_rgb(229_231_235)]';
  const rowStyle = { height: ROW_H };

  /**
   * 차주 머리줄 — 지급·공제 칸 자리에 소계, 그 밖의 칸은 이어 붙여 한 칸으로(칸 순서가 바뀌어도 소계가 제 칸 밑에).
   * 차주 이름은 금액 아닌 첫 묶음에. 맨 끝 버튼 칸은 마지막 묶음에 붙인다.
   */
  const groupCells = (g: MoGroup) => {
    const out: ReactNode[] = [];
    let run = 0; let labeled = false;
    const flush = (last = false) => {
      const span = run + (last && canEdit ? 1 : 0);
      if (span === 0) return;
      if (!labeled && run > 0) {
        labeled = true;
        out.push(
          <td key={`l${out.length}`} className={`${td} font-semibold ${phone ? 'sticky left-0 bg-canvas' : ''}`} colSpan={span}
              title={`차주 ${g.owner_name ?? '(없음)'} #${g.owner_id ?? '—'}`}>
            차주 {g.owner_name ?? '(없음)'}
            <span className="ml-1 font-normal text-ink-faint">#{g.owner_id ?? '—'} · {g.n.toLocaleString('ko-KR')}건</span>
          </td>,
        );
      } else {
        out.push(<td key={`e${out.length}`} colSpan={span} />);
      }
      run = 0;
    };
    for (const c of cols) {
      if (c.key !== 'plus' && c.key !== 'minus') { run += 1; continue; }
      flush();
      out.push(c.key === 'plus'
        ? <td key="plus" className={`${td} text-right font-semibold text-emerald-700`}>{Number(g.plus) ? money(g.plus) : ''}</td>
        : <td key="minus" className={`${td} text-right font-semibold text-red-700`}>{Number(g.minus) ? money(g.minus) : ''}</td>);
    }
    flush(true);
    return out;
  };

  return (
    <div ref={scroller} className="min-h-64 overflow-auto" style={{ maxHeight: paneH }}
         onScroll={(e) => setVp({ top: e.currentTarget.scrollTop, h: e.currentTarget.clientHeight })}>
      <table className={`w-full table-fixed text-xs ${loading ? 'opacity-60' : ''}`} style={{ minWidth: totalW }}>
        <colgroup>
          {canEdit && <col style={{ width: 28 }} />}
          {cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
          {canEdit && <col style={{ width: actW }} />}
        </colgroup>
        <thead>
          <tr>
            {canEdit && (
              <th className={th}>
                {/* 머리 전체 고르기(레거시 그리드 머리 체크) — 지금 걸린 검색·거르기 결과 **전부**(보이는 쪽만이 아니다). */}
                <input ref={headBox} type="checkbox" checked={allOn} disabled={selectable.length === 0}
                       title={`지금 걸린 ${selectable.length.toLocaleString('ko-KR')}건 전부 ${allOn ? '빼기' : '고르기'}`}
                       onChange={() => toggle(selectable, !allOn)} />
              </th>
            )}
            {cols.map((c) => (
              <th key={c.key} draggable={!phone}
                  title={phone ? undefined : '머리글을 끌어 다른 칸 자리에 놓으면 칸 순서가 바뀝니다'}
                  onDragStart={(e) => {
                    // 칸 폭 손잡이를 끄는 중이면 칸 옮기기가 아니다(#257)
                    if (resizing.current) { e.preventDefault(); return; }
                    // ▾ 거르기 창(포털)에서 시작한 끌기는 칸 옮기기가 아니다
                    if (!e.currentTarget.contains(e.target as Node)) return;
                    dragKey.current = c.key;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', c.key);
                  }}
                  onDragOver={(e) => {
                    if (!dragKey.current || dragKey.current === c.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dropOn !== c.key) setDropOn(c.key);
                  }}
                  onDragLeave={() => setDropOn((k) => (k === c.key ? null : k))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragKey.current;
                    dragKey.current = null; setDropOn(null);
                    if (from && from !== c.key) setColOrder(moveCol(colOrder, from, c.key));
                  }}
                  onDragEnd={() => { dragKey.current = null; setDropOn(null); }}
                  className={`${th} ${c.right ? 'text-right' : ''} ${phone ? '' : 'cursor-grab'} ${dropOn === c.key ? '!bg-brand-50 outline outline-2 -outline-offset-2 outline-brand-400' : ''} ${phone && c.pin ? 'left-0 z-20 shadow-[1px_0_0_rgb(229_231_235)]' : ''}`}>
                <button type="button" className="hover:text-ink" title="누르면 오름차순 → 내림차순 → 원래 순서"
                        onClick={() => setSort(sort?.key !== c.key ? { key: c.key, dir: 'asc' } : sort.dir === 'asc' ? { key: c.key, dir: 'desc' } : null)}>
                  {c.label}{sort?.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
                <ColumnFilter label={c.label} selected={filters[c.key]}
                              getValues={() => valuesFor(allRows, filters, c.key)}
                              onApply={(s) => setFilter(c.key, s)} />
                {!phone && (
                  <span role="separator" aria-orientation="vertical" title="끌어서 칸 폭 바꾸기"
                        className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-brand-200"
                        onPointerDown={(e) => startResize(e, c)}
                        onClick={(e) => e.stopPropagation()} />
                )}
              </th>
            ))}
            {canEdit && <th className={th} />}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={ncol} className="p-6 text-center text-sm text-ink-faint">{loading ? '불러오는 중…' : empty}</td></tr>
          )}
          {start > 0 && <tr aria-hidden style={{ height: start * rowH }}><td colSpan={ncol} className="p-0" /></tr>}
          {items.slice(start, end).map(({ g, r }) => {
            if (!r) {
              const ids = g.rows.filter((x) => pickable(side, x)).map((x) => keyOf(side, x)!);
              const all = ids.length > 0 && ids.every((id) => picked.has(id));
              return (
                <tr key={`g-${g.owner_id}`} data-item style={rowStyle} className="border-b border-line/60 bg-canvas">
                  {canEdit && (
                    <td className="px-2">
                      <input type="checkbox" checked={all} disabled={ids.length === 0}
                             title="이 차주 줄 모두 고르기" onChange={() => toggle(ids, !all)} />
                    </td>
                  )}
                  {groupCells(g)}
                </tr>
              );
            }
            const k = keyOf(side, r);
            const moved = side === 'def' && r.result_mo_id != null;
            return (
              <tr key={`${r.result_mo_def_id}-${r.result_mo_id}`} data-item style={rowStyle}
                  title={canEdit ? '두 번 누르면 수정 창' : undefined}
                  onDoubleClick={canEdit ? (e) => {
                    // #341 구 버스인처럼 줄 두 번 누름 = 수정 창(네모·버튼 위는 그대로)
                    if ((e.target as HTMLElement).closest('input,button,a,select')) return;
                    window.getSelection()?.removeAllRanges();
                    onEdit(r);
                  } : undefined}
                  className={`border-b border-line/60 ${canEdit ? 'cursor-pointer' : ''} ${moved ? 'text-ink-faint' : ''} ${k != null && picked.has(k) ? 'bg-brand-50' : 'bg-surface'}`}>
                {canEdit && (
                  <td className="px-2">
                    <input type="checkbox" disabled={!pickable(side, r)} checked={k != null && picked.has(k)}
                           onChange={() => k != null && toggle([k], !picked.has(k))} />
                  </td>
                )}
                {cols.map((c) => (
                  <td key={c.key} title={c.title?.(r)}
                      className={`${td} ${c.right ? 'text-right tabular-nums' : ''} ${c.key === 'no' || c.key === 'created' ? 'text-ink-soft' : ''} ${phone && c.pin ? pinTd : ''}`}>
                    {c.cell(r)}
                  </td>
                ))}
                {canEdit && (
                  <td className="whitespace-nowrap px-1">
                    {side === 'month'
                      ? <button className="btn-ghost px-1.5 py-0.5 text-xs" onClick={() => onEdit(r)}>금액수정</button>
                      : (
                        <span className="flex whitespace-nowrap">
                          <button className="btn-ghost px-1.5 py-0.5 text-xs" title="정기항목(설정값)의 계정·금액·비고를 고칩니다 — 다음 등록부터 적용"
                                  onClick={() => onEdit(r)}>수정</button>
                          <button className="btn-ghost px-1.5 py-0.5 text-xs" title="기사·차량·차주·소속을 편성에서 골라 바꿉니다"
                                  onClick={() => onReassign(r)}>편성변경</button>
                          {onDelete && (
                            <button className="btn-ghost px-1.5 py-0.5 text-xs text-red-600" title="정기항목 삭제 — 이미 등록된 달 줄은 남습니다"
                                    onClick={() => onDelete(r)}>삭제</button>
                          )}
                        </span>
                      )}
                  </td>
                )}
              </tr>
            );
          })}
          {end < items.length && <tr aria-hidden style={{ height: (items.length - end) * rowH }}><td colSpan={ncol} className="p-0" /></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** 계정별 합계(#406) — 지금 걸린 검색·거르기 결과 전부(쪽과 무관)를 계정마다 한 줄로. 차주별 소계는 표에 그대로. */
function AccountTotalsView({ title, view, filtered, onClose }: {
  title: string; view: MoGroup[]; filtered: boolean; onClose: () => void;
}) {
  const rows = useMemo(() => accountTotals(view), [view]);
  const sum = rows.reduce((s, r) => ({ n: s.n + r.n, plus: s.plus + r.plus, minus: s.minus + r.minus }), { n: 0, plus: 0, minus: 0 });
  const owners = view.length;
  return (
    <div className="card flex max-h-[85vh] w-full max-w-2xl flex-col p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">{title} 계정별 합계</h2>
        {filtered && <span className="rounded bg-brand-50 px-1 text-xs text-brand-700">거른 결과</span>}
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      <p className="mt-1 text-xs text-ink-soft">지금 검색·거르기에 걸린 줄 전부(쪽과 무관)를 계정마다 더했습니다.</p>
      <div className="mt-3 min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface">
            <tr className="border-b border-line text-left text-ink-soft">
              <th className="px-2 py-1.5">계정</th>
              <th className="px-2 py-1.5 text-right">건수</th>
              <th className="px-2 py-1.5 text-right">차주</th>
              <th className="px-2 py-1.5 text-right">지급</th>
              <th className="px-2 py-1.5 text-right">공제</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.account} className="border-b border-line">
                <td className="px-2 py-1">{r.account || '(계정 없음)'}</td>
                <td className="px-2 py-1 text-right">{r.n.toLocaleString('ko-KR')}</td>
                <td className="px-2 py-1 text-right">{r.owners.toLocaleString('ko-KR')}</td>
                <td className="px-2 py-1 text-right text-emerald-700">{r.plus ? money(r.plus) : ''}</td>
                <td className="px-2 py-1 text-right text-red-700">{r.minus ? money(r.minus) : ''}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-ink-soft">걸린 줄이 없습니다.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-surface font-bold">
              <tr className="border-t-2 border-line">
                <td className="px-2 py-1.5">전체 ({rows.length.toLocaleString('ko-KR')}개 계정)</td>
                <td className="px-2 py-1.5 text-right">{sum.n.toLocaleString('ko-KR')}</td>
                <td className="px-2 py-1.5 text-right">{owners.toLocaleString('ko-KR')}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">{money(sum.plus)}</td>
                <td className="px-2 py-1.5 text-right text-red-700">{money(sum.minus)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/**
 * 「전체등록」 확인 팝업(10-30 way) — 그 달 미등록 정기항목 **전부**가 서버에서 한 번에 들어간다.
 * 잘못 누르는 일이 잦아 window.confirm(반사적으로 확인) 대신 등록할 달을 그대로 쳐야 버튼이 열린다.
 * 검색·거르기·고른 줄이 있으면 버튼 자체가 꺼진다(그땐 「선택등록」 — way 캡처 10-30).
 */
function ApplyAllConfirm({
  mon, registered, onClose, onConfirm,
}: {
  mon: string; registered: number | null; onClose: () => void; onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const ok = typed.trim() === mon;
  return (
    <div className="card space-y-4 p-5">
      <h2 className="text-lg font-bold text-red-700">정말 「전체등록」할까요?</h2>
      <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-900">
        <p><b className="text-base">{mon}</b> 월정산에 <b>아직 안 넘긴 정기항목 전부</b>가 한 번에 등록됩니다.</p>
        {registered != null && <p className="mt-1">지금 오른쪽에 이미 {registered.toLocaleString('ko-KR')}건이 있습니다(이미 등록된 것은 건너뜁니다).</p>}
      </div>
      <label className="block text-sm">
        전체등록하려면 등록할 달 <b>{mon}</b> 을 그대로 입력하세요.
        <input className="field mt-1 w-full" autoFocus value={typed} placeholder={mon}
               onChange={(e) => setTyped(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter' && ok) onConfirm(); }} />
      </label>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>취소</button>
        <button className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" disabled={!ok} onClick={onConfirm}>
          {mon} 전체등록
        </button>
      </div>
    </div>
  );
}

/** 「선택등록」 확인 팝업(10-30 way) — 고른 몇 건이 어느 달로 가는지 한 번 보여 준다. */
function ApplyPickedConfirm({
  mon, lines, owners, q, onClose, onConfirm,
}: {
  mon: string; lines: number; owners: number; q: string; onClose: () => void; onConfirm: () => void;
}) {
  return (
    <div className="card space-y-4 p-5">
      <h2 className="text-lg font-bold">선택등록</h2>
      <div className="rounded border border-brand-200 bg-brand-50 px-4 py-3 text-sm leading-relaxed">
        <p>왼쪽에서 고른 <b className="text-base">{lines.toLocaleString('ko-KR')}건</b>(차주 {owners.toLocaleString('ko-KR')}명)을
          {' '}<b className="text-base">{mon}</b> 월정산에 등록합니다.</p>
        {q && <p className="mt-1 text-ink-soft">검색어 「{q}」에 걸린 줄 중 고른 것만 — 나머지 정기항목은 그대로입니다.</p>}
        <p className="mt-1 text-ink-soft">이미 등록된 것과 금액 0원은 건너뜁니다.</p>
      </div>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>취소</button>
        <button className="btn-primary" autoFocus onClick={onConfirm}>{lines.toLocaleString('ko-KR')}건 등록</button>
      </div>
    </div>
  );
}

/** 머리 줄 — 합계는 거르기가 걸리면 **거른 결과**로(서버 합계는 검색 전부). */

function PaneHead({
  title, data, view, filtered, allRows, filters, setFilters, picked, children,
}: {
  title: string; data: Board | null; view: MoGroup[]; filtered: boolean; allRows: MoLine[];
  filters: ColFilters; setFilters: (f: ColFilters) => void; picked: number | null; children?: ReactNode;
}) {
  const nFilter = activeFilterCount(filters);
  const sum = useMemo(() => {
    if (!filtered) return null;
    let plus = 0; let minus = 0; let n = 0;
    for (const g of view) { plus += Number(g.plus); minus += Number(g.minus); n += g.n; }
    return { owners: view.length, n, plus, minus };
  }, [filtered, view]);
  const s = sum ?? (data ? { owners: data.owners, n: data.n, plus: data.plus, minus: data.minus } : null);
  const [showTotals, setShowTotals] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3 py-2 text-sm">
      <b>{title}</b>
      {s && (
        <>
          <span className="text-ink-soft">
            {sum && <span className="mr-1 rounded bg-brand-50 px-1 text-xs text-brand-700">거른 결과</span>}
            차주 {s.owners.toLocaleString('ko-KR')} · {s.n.toLocaleString('ko-KR')}건
          </span>
          <span className="text-emerald-700">지급 <b>{money(s.plus)}</b></span>
          <span className="text-red-700">공제 <b>{money(s.minus)}</b></span>
          <button className="btn-ghost px-2 py-0.5 text-xs" disabled={view.length === 0} onClick={() => setShowTotals(true)}
                  title="지금 걸린 결과를 계정마다 합해 봅니다(쪽과 무관)">계정별 합계</button>
        </>
      )}
      {showTotals && (
        <Modal>
          <AccountTotalsView title={title} view={view} filtered={filtered} onClose={() => setShowTotals(false)} />
        </Modal>
      )}
      <span className="text-xs text-ink-soft">
        차주
        <ColumnFilter label="차주" selected={filters.owner}
                      getValues={() => valuesFor(allRows, filters, 'owner')}
                      onApply={(sel) => setFilters({ ...filters, owner: sel })} />
      </span>
      {nFilter > 0 && (
        <button className="text-xs text-brand-700 underline" onClick={() => setFilters({})}
                title="칼럼 머리글 ▾ 로 건 거르기를 모두 풉니다">거르기 {nFilter}개 풀기</button>
      )}
      {picked !== null && picked > 0 && <span className="text-xs font-medium text-brand-700">{picked.toLocaleString('ko-KR')}건 고름</span>}
      <span className="ml-auto flex items-center gap-2">{children}</span>
    </div>
  );
}

/** 아래 줄 — 쪽 넘기기(차주 30명씩) 또는 「전체」(한 번에 스크롤). 고른 방식은 이 브라우저에 기억한다(#96 과 같은 방식). */
function PanePager({
  owners, page, setPage, per, setPer,
}: { owners: number; page: number; setPage: (p: number) => void; per: number; setPer: (n: number) => void }) {
  const last = per >= PAGE_ALL ? 0 : Math.max(Math.ceil(owners / per) - 1, 0);
  return (
    <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-1.5 text-xs">
      <select className="field w-auto py-0.5 text-xs" value={per} title="한 번에 보여 줄 차주 수"
              onChange={(e) => { setPer(Number(e.target.value)); setPage(0); }}>
        <option value={OWNERS_PER_PAGE}>차주 {OWNERS_PER_PAGE}명씩</option>
        <option value={PAGE_ALL}>전체 (한 번에 스크롤)</option>
      </select>
      {last > 0
        ? (
          <>
            <button className="btn-ghost px-2 py-0.5 text-xs" disabled={page === 0} onClick={() => setPage(page - 1)}>이전</button>
            <span className="text-ink-soft">
              차주 {(page * per + 1).toLocaleString('ko-KR')}–{Math.min((page + 1) * per, owners).toLocaleString('ko-KR')} / {owners.toLocaleString('ko-KR')}
            </span>
            <button className="btn-ghost px-2 py-0.5 text-xs" disabled={page >= last} onClick={() => setPage(page + 1)}>다음</button>
          </>
        )
        : <span className="text-ink-soft">차주 {owners.toLocaleString('ko-KR')}명 전부</span>}
    </div>
  );
}

/** 엑셀 받기 — 한 쪽(정기항목 또는 월정산)의 **검색·거르기에 걸린 줄 전부**(쪽과 무관). 금액·번호는 숫자 칸으로. */
async function exportSide(side: Side, groups: MoGroup[], mon: string) {
  const head = ['차주', '차주 ID', ...(side === 'month' ? ['NO'] : []), '기사', '차량', '소속', '계정', '지급', '공제',
    '편성 ID', '기사 ID', '비고', '등록일', side === 'def' ? `${mon} 등록` : '구분'];
  const num = (v: unknown) => (v === null || v === undefined || v === '' ? '' : Number(v));
  const body = groups.flatMap((g) => g.rows.map((r) => [
    r.owner_name ?? '', num(r.owner_id),
    ...(side === 'month' ? [num(r.result_mo_id)] : []),
    r.driver_name ?? '', r.car_no ?? '', r.grp_name ?? '', r.account_name ?? '',
    r.is_plus ? num(r.amt) : '', r.is_plus ? '' : num(r.amt),
    num(r.car_alloc_id), num(r.driver_id), r.comment ?? '', r.created_at ?? '',
    side === 'def' ? (r.result_mo_id != null ? '등록됨' : '') : (r.result_mo_def_id == null ? '수동' : '정기항목'),
  ]));
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), side === 'def' ? '정기항목' : `${mon} 월정산`);
  const d = new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
  XLSX.writeFile(wb, side === 'def' ? `월정기_정기항목_${mon}_${d}.xlsx` : `월정기_${mon}_월정산_${d}.xlsx`);
}

/**
 * 월정기 — 레거시 「월정기 정산등록」처럼 **왼쪽 정기항목 → 오른쪽 그 달 월정산**(2026-09-30 way).
 * 가운데 버튼으로 넘기고(전체등록·선택등록) 되돌린다(선택취소·전체취소). 양쪽 모두 차주별로 묶고 소계를 붙인다.
 * 왼쪽은 기본으로 **아직 안 넘긴 것만** 보여, 넘기면 왼쪽에서 빠지고 오른쪽에 생긴다.
 *
 * 10-02 함남열: 양쪽 엑셀 받기 · 칼럼 머리글 ▾ 거르기 · 머리 전체 고르기(걸린 결과 전부) · 「전체」 한 번에 스크롤.
 * 양쪽 다 그 달 전부를 받아 화면에서 거른다(`useBoard`) — 고르기는 늘 지금 걸린 결과 안에서만(`lib/moBoard.ts`).
 */
export default function ResultMo() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'result-mo');

  const [months, setMonths] = useState<string[]>([]);
  // 고른 정산년월은 화면을 옮겨도 남는다 — 담당자는 한 달을 여러 화면에서 처리한다.
  const [mon, setMon] = useStickyDriveMon();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  // 왼쪽은 버스인처럼 **설정값 전부를 상시** 보인다(2026-09-30 way·#84) — 등록된 줄은 흐리게 「등록됨」, 고를 수 없다.
  const [pendingOnly, setPendingOnly] = useState(false);
  // 10-30 송선우(강범삼) — 편성이 끝난 사람의 정기항목은 기본 목록·전체등록에서 빠지고 여기서 따로 본다.
  const [endedView, setEndedView] = useState(false);
  const [defEdit, setDefEdit] = useState<Row | 'new' | null>(null);
  const [lPage, setLPage] = useState(0);
  const [rPage, setRPage] = useState(0);
  // 차주 30명씩(기본 — 예전 그대로) / 전체(한 번에 스크롤). 양쪽이 같이 따르고 이 브라우저에 기억한다.
  const [per, setPer] = usePageSize('result-mo-board', OWNERS_PER_PAGE);
  const [colOrder, setColOrder] = useColOrder();
  const [colWidths, setColWidths] = useColWidths();
  const orderHint = (
    <>
      {' '}머리글을 끌어 옮기면 칸 순서가, 머리글 오른쪽 끝을 끌면 칸 폭이 바뀝니다(양쪽 같이 · 이 PC에 기억).
      {colOrder && (
        <button className="ml-1 text-brand-700 underline" onClick={() => setColOrder(null)}>칸 순서 처음대로</button>
      )}
      {colWidths && (
        <button className="ml-1 text-brand-700 underline" onClick={() => setColWidths(null)}>칸 폭 처음대로</button>
      )}
    </>
  );
  const [lFilters, setLFiltersRaw] = useState<ColFilters>({});
  const [rFilters, setRFiltersRaw] = useState<ColFilters>({});
  const [lSort, setLSort] = useState<MoSort>(null);
  const [rSort, setRSort] = useState<MoSort>(null);
  const [nonce, setNonce] = useState(0);
  const [lPicked, setLPicked] = useState<Set<number>>(new Set());
  const [rPicked, setRPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [editing, setEditing] = useState<Row | null>(null);
  const [reassigning, setReassigning] = useState<Row[] | null>(null);
  // #328 — 고른 정기항목 여러 개 한 번에 고치기(금액·계정명)
  const [bulkEdit, setBulkEdit] = useState<number[] | null>(null);
  // #415 — 방금 넘긴 묶음. 「전체취소」는 앞서 등록해 둔 줄까지 내리므로(10-27 용인·파주 751줄 사고)
  // 잘못 누른 등록은 이 번호만 되돌린다. 달이 바뀌거나 다른 취소를 하면 지운다.
  const [lastBatch, setLastBatch] = useState<{ mon: string; ids: number[] } | null>(null);
  useEffect(() => { setLastBatch(null); }, [mon]);
  const setLFilters = (f: ColFilters) => { setLFiltersRaw(f); setLPage(0); };
  const setRFilters = (f: ColFilters) => { setRFiltersRaw(f); setRPage(0); };

  // 치는 대로 찾는다(입력이 멎으면 적용). 검색어는 **양쪽에 같이** 걸린다 — 한 사람의 정기항목과 이 달 줄을 나란히 본다.
  useAutoSearch(term, (v) => { setQ(v); setLPage(0); setRPage(0); });
  // 다른 화면에서 건너온 링크(?q=·?mon= — 정산현황 줄 보기 · lib/deepLink.ts)면 그 검색어로.
  useUrlSearch(setTerm, setQ, setMon);

  useEffect(() => {
    api.listDriveMonths()
      .then((r) => { setMonths(r.rows); setMon(pickDriveMon(r.rows, mon)); })
      .catch(() => undefined);
    // 마운트 때 한 번만 — `mon` 은 기억된 값을 읽는 용도다(의존성에 넣으면 월마다 재조회).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const left = useBoard('def', mon, q, pendingOnly, nonce, endedView);
  const right = useBoard('month', mon, q, true, nonce);
  const finished = Boolean(right.data?.isFinished ?? left.data?.isFinished);

  // 검색·거르기에 걸린 차주 묶음(모든 쪽) → 고를 수 있는 번호 → 고름을 그 안으로 줄인다.
  const lAll = useMemo(() => left.data?.groups ?? [], [left.data]);
  const rAll = useMemo(() => right.data?.groups ?? [], [right.data]);
  const lRows = useMemo(() => lAll.flatMap((g) => g.rows), [lAll]);
  const rRows = useMemo(() => rAll.flatMap((g) => g.rows), [rAll]);
  const lView = useMemo(() => sortGroups(filterGroups(lAll, lFilters), lSort), [lAll, lFilters, lSort]);
  const rView = useMemo(() => sortGroups(filterGroups(rAll, rFilters), rSort), [rAll, rFilters, rSort]);
  const lSel = useMemo(() => selectableIds('def', lView), [lView]);
  const rSel = useMemo(() => selectableIds('month', rView), [rView]);
  useEffect(() => { setLPicked((p) => pruneTo(p, new Set(lSel))); }, [lSel]);
  useEffect(() => { setRPicked((p) => pruneTo(p, new Set(rSel))); }, [rSel]);
  // 「선택등록」·「선택취소」·가운데 숫자가 쓰는 번호 = 고른 것 ∩ 지금 걸린 결과.
  const lSend = useMemo(() => idsToSend('def', lPicked, lView), [lPicked, lView]);
  const rSend = useMemo(() => idsToSend('month', rPicked, rView), [rPicked, rView]);
  const pageOf = (v: MoGroup[], p: number) => (per >= PAGE_ALL ? v : v.slice(p * per, (p + 1) * per));
  const lPageGroups = useMemo(() => pageOf(lView, lPage), [lView, lPage, per]); // eslint-disable-line react-hooks/exhaustive-deps
  const rPageGroups = useMemo(() => pageOf(rView, rPage), [rView, rPage, per]); // eslint-disable-line react-hooks/exhaustive-deps

  function after(msg: string) {
    setLPicked(new Set()); setRPicked(new Set());
    setNotice(msg); setNonce((n) => n + 1);
  }

  // 10-30 way — 「전체등록」 잘못 누름이 잦다(그날 송선우 2,244줄 · 10-27 홍명옥 2,984줄) → 확인창 대신 달을 직접 쳐야 열리는 팝업.
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmPicked, setConfirmPicked] = useState(false);
  // 검색·거르기·고른 줄이 있으면 사람 뜻은 「그것만」이다 → 전체등록을 끈다(way 캡처 10-30 「gps」 검색 뒤 285건 고름).
  const lNarrowed = Boolean(q) || activeFilterCount(lFilters) > 0;
  const allBlocked = lNarrowed || lSend.length > 0 || endedView;
  const pickedOwners = useMemo(() => {
    const ids = new Set(lSend);
    return lView.filter((g) => g.rows.some((r) => ids.has(keyOf('def', r) ?? -1))).length;
  }, [lSend, lView]);

  async function apply(all: boolean) {
    setConfirmAll(false); setConfirmPicked(false);
    setBusy(true); setError(null); setNotice(null);
    try {
      // 전체등록은 **서버가** 그 달 미등록 정기항목 전량을 처리한다(목록 페이지에 안 묶인다 — issues 65).
      // 선택등록은 **지금 걸린 결과 안에서 고른 것만**(lSend — 걸러서 사라진 줄은 안 간다).
      const r = all
        ? await api.applyResultMo({ driveMon: mon, all: true })
        : await api.applyResultMo({ driveMon: mon, defIds: lSend });
      setLastBatch(r.createdIds?.length ? { mon, ids: r.createdIds } : null);
      after(
        `${r.created.toLocaleString('ko-KR')}건 등록`
        + (r.skippedAlready > 0 ? ` · ${r.skippedAlready.toLocaleString('ko-KR')}건은 이미 등록돼 건너뜀` : '')
        + (r.skippedZero > 0 ? ` · ${r.skippedZero.toLocaleString('ko-KR')}건은 금액 0(휴면)이라 제외` : '')
        + ((r.skippedEnded ?? 0) > 0 ? ` · ${(r.skippedEnded ?? 0).toLocaleString('ko-KR')}건은 편성 종료라 제외` : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  // #415 🔔 — 성명으로 거르고 그 사람 줄을 고른 뒤 「전체취소」를 눌러 그 달 전부가 내려갔다.
  // 화면에 걸린 게 있으면 「전체」는 **보이는 것 전부**다: 고른 줄이 있으면 그것만, 검색·거르기만 있으면
  // 걸린 결과 중 정기항목에서 온 줄만(서버 전체취소와 같은 기준 — 수동 등록분은 남김).
  const rNarrowed = Boolean(q) || activeFilterCount(rFilters) > 0;
  const rViewDefIds = useMemo(
    () => rView.flatMap((g) => g.rows).filter((r) => r.result_mo_def_id != null && r.result_mo_id != null)
      .map((r) => Number(r.result_mo_id)),
    [rView],
  );
  const cancelAllTarget: 'picked' | 'view' | 'all' = rSend.length > 0 ? 'picked' : rNarrowed ? 'view' : 'all';
  const cancelAllLabel = cancelAllTarget === 'picked'
    ? `« 고른 ${rSend.length.toLocaleString('ko-KR')}건 취소`
    : cancelAllTarget === 'view' ? `« 걸린 ${rViewDefIds.length.toLocaleString('ko-KR')}건 취소` : '« 전체취소';

  async function cancelAll() {
    if (cancelAllTarget === 'all') return cancel(true);
    const ids = cancelAllTarget === 'picked' ? rSend : rViewDefIds;
    if (ids.length === 0) return;
    if (!window.confirm(
      cancelAllTarget === 'picked'
        ? `고른 ${ids.length.toLocaleString('ko-KR')}건만 등록취소합니다(나머지 줄은 그대로). 계속할까요?`
        : `지금 검색·거르기에 걸린 ${ids.length.toLocaleString('ko-KR')}건만 등록취소합니다(걸리지 않은 줄·수동 등록분은 그대로). 계속할까요?`,
    )) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.cancelResultMo({ driveMon: mon, resultMoIds: ids });
      setLastBatch(null);
      after(`${r.cancelled.toLocaleString('ko-KR')}건 등록취소`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function cancel(all: boolean) {
    if (all && !window.confirm(
      `${mon} 월정산에서 정기항목으로 등록한 줄을 모두 취소합니다${q || activeFilterCount(rFilters) ? '(검색·거르기와 상관없이 전부)' : ''}.\n오늘 이전에 등록해 둔 줄까지 전부 취소됩니다.`
      + (lastBatch ? `\n방금 등록한 ${lastBatch.ids.length.toLocaleString('ko-KR')}건만 되돌리려면 「취소」를 누르고 「방금 등록 되돌리기」를 쓰세요.` : '')
      + '\n수동 등록분은 남습니다. 계속할까요?',
    )) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = all
        ? await api.cancelResultMo({ driveMon: mon, all: true })
        : await api.cancelResultMo({ driveMon: mon, resultMoIds: rSend });
      setLastBatch(null);
      after(`${r.cancelled.toLocaleString('ko-KR')}건 등록취소`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function undoBatch() {
    if (!lastBatch || lastBatch.mon !== mon) return;
    if (!window.confirm(`방금 등록한 ${lastBatch.ids.length.toLocaleString('ko-KR')}건만 등록취소합니다(그 전에 등록한 줄은 그대로). 계속할까요?`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.cancelResultMo({ driveMon: mon, resultMoIds: lastBatch.ids });
      setLastBatch(null);
      after(`방금 등록한 ${r.cancelled.toLocaleString('ko-KR')}건 되돌림`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function download(side: Side) {
    setExporting(side); setError(null);
    try {
      await exportSide(side, side === 'def' ? lView : rView, mon);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setExporting(null); }
  }

  async function removeDef(r: MoLine) {
    if (!window.confirm(`${dash(r.driver_name)} ${dash(r.car_no)} 의 정기항목 “${dash(r.account_name)} ${money(r.amt)}”을(를) 삭제할까요?\n이미 등록된 달 줄은 그대로 남습니다.`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await api.deleteResultMoDef({ resultMoDefId: Number(r.result_mo_def_id) });
      const open = res.openMonthsStillApplied;
      after('정기항목을 삭제했습니다.'
        + (open.length > 0 ? ` 아직 마감되지 않은 ${open.join(', ')} 에는 등록된 줄이 남아 있습니다 — 필요하면 오른쪽에서 취소하세요.` : ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  /** #328 — 고른 정기항목을 한 번에 지운다(한 트랜잭션 · 이미 등록된 달 줄은 남는다). */
  async function removeDefs(defIds: number[]) {
    if (defIds.length === 0) return;
    if (!window.confirm(`고른 정기항목 ${defIds.length.toLocaleString('ko-KR')}건을 삭제할까요?\n이미 등록된 달 줄은 그대로 남습니다.`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await api.deleteResultMoDef({ resultMoDefIds: defIds });
      const open = res.openMonthsStillApplied;
      setLPicked(new Set());
      after(`정기항목 ${res.deletedCount.toLocaleString('ko-KR')}건을 삭제했습니다.`
        + (res.skipped > 0 ? ` ${res.skipped}건은 이미 삭제돼 건너뜀.` : '')
        + (open.length > 0 ? ` 아직 마감되지 않은 ${open.join(', ')} 에는 등록된 줄이 남아 있습니다 — 필요하면 오른쪽에서 취소하세요.` : ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  // 편성변경 편집기는 예전 목록 모양(def_account_name)을 읽는다.
  const asReassignRow = (r: MoLine): Row => ({ ...r, def_account_name: r.account_name } as unknown as Row);

  const mid = 'btn-ghost w-full justify-center whitespace-nowrap px-2 text-xs';
  const nLines = (v: MoGroup[]) => v.reduce((s, g) => s + g.n, 0);
  const xlsBtn = (side: Side, n: number) => (
    <button className="btn-ghost px-2 py-1 text-xs" disabled={exporting !== null || n === 0} onClick={() => void download(side)}
            title="검색·거르기에 걸린 줄 전부(쪽과 상관없이)를 엑셀로 받습니다">
      {exporting === side ? '받는 중…' : '엑셀 받기'}
    </button>
  );
  return (
    <div className="space-y-2">
      {/* 상단은 한 줄(10-27 way 「상단 메뉴 폭 극소화 · 하단 작업 내용 중심」) — 제목·설명(한 줄, 올리면 펼침)·정산년월·검색·편성 변경·정기항목 관리.
          운행편성 변경(10-02)은 이 정산월에 편성이 바뀐 차량이 있으면 늘 보인다(펼치면 줄 하나를 쓴다). */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="page-head min-w-0 flex-1">
          <h1 className="font-bold">월정기</h1>
          <p>
            왼쪽 <b>정기항목</b>은 매달 쓰는 설정값입니다(등록·수정·삭제는 여기서). 골라서 가운데 버튼으로 넘기면
            오른쪽 <b>{mon || '그 달'} 월정산</b>에 복사돼 급여명세에 반영되고, 왼쪽 설정값은 그대로 남습니다(넘긴 줄은 「등록됨」).
            같은 달에 두 번 넘겨도 중복되지 않습니다. 칼럼 머리글 <b>▾</b> 로 값을 골라 거를 수 있습니다.
          </p>
        </div>
        <DriveMonPicker
          months={months}
          value={mon}
          onChange={(m) => { setMon(m); setLPage(0); setRPage(0); setLPicked(new Set()); setRPicked(new Set()); }}
        />
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setLPage(0); setRPage(0); }}
              className="flex items-center gap-2">
          <input className="field w-64" placeholder="계정·차주·기사·차량번호·NO (여러 개는 띄어쓰기)"
                 title="양쪽에 같이 걸립니다. 계정 이름을 그대로 치면 그 계정만 찾습니다(예: GPS이용료 공제금 조합비)."
                 value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        {finished && (
          <span className="rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
            {mon} 월정산 마감됨 — 등록·취소·수정이 막힙니다
          </span>
        )}
        <AllocChangeBanner mon={mon} compact />
        {/* 표 아래 설명 두 줄은 여기로(10-27 way 「하단 설명 없어도 됨」) */}
        <CalcNote label="ⓘ 도움말">
          <p>금액 0(휴면) 정기항목은 등록 대상이 아니라 빠져 있습니다 — 정기항목 관리에서 금액을 채우면 나타납니다.</p>
          <p>머리글 네모 = 지금 걸린 결과 전부 고르기(다른 쪽 차주까지). 취소는 그 달 줄만 내립니다 — 정기항목은 남아 다시 넘길 수 있습니다. 금액수정은 이 달만 바뀝니다.</p>
          <p>{orderHint}</p>
        </CalcNote>
        <Link className="btn-ghost text-sm" to="/result-mo-def">정기항목 관리</Link>
      </div>

      {error && <ErrorBox message={error} />}
      {left.error && <ErrorBox message={left.error} />}
      {right.error && <ErrorBox message={right.error} />}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_88px_minmax(0,1fr)]">
        <section className="card flex min-w-0 flex-col overflow-hidden">
          <PaneHead title="정기항목" data={left.data} view={lView} filtered={activeFilterCount(lFilters) > 0} allRows={lRows}
                    filters={lFilters} setFilters={setLFilters} picked={canEdit ? lSend.length : null}>
            <label className="flex items-center gap-1 text-xs text-ink-soft">
              <input type="checkbox" checked={pendingOnly}
                     onChange={(e) => { setPendingOnly(e.target.checked); setLPage(0); setLPicked(new Set()); }} />
              안 넘긴 것만
            </label>
            {((left.data?.endedCount ?? 0) > 0 || endedView) && (
              <button className={`btn-ghost px-2 py-1 text-xs ${endedView ? 'border-amber-400 bg-amber-50 font-semibold text-amber-800' : 'text-ink-soft'}`}
                      title="편성이 모두 이 달 전에 끝난 사람의 정기항목 — 목록·전체등록에서 빠져 있습니다. 필요 없으면 지우고, 넣어야 하면 골라 「선택등록」"
                      onClick={() => { setEndedView((v) => !v); setLPage(0); setLPicked(new Set()); }}>
                {endedView ? '← 정기항목으로' : `편성 종료 ${(left.data?.endedCount ?? 0).toLocaleString('ko-KR')}`}
              </button>
            )}
            {canEdit && (
              <button className="btn-ghost px-2 py-1 text-xs" disabled={busy} onClick={() => setDefEdit('new')}
                      title="새 정기항목(매달 반복되는 지급·공제)을 등록합니다">
                + 정기항목 등록
              </button>
            )}
            {canEdit && (
              <button className="btn-ghost px-2 py-1 text-xs" disabled={busy || lSend.length === 0}
                      title="고른 항목을 한 편성으로 옮깁니다 — 기사가 차를 바꿨을 때"
                      onClick={() => {
                        const send = new Set(lSend);
                        setReassigning(lRows.filter((r) => send.has(Number(r.result_mo_def_id))).map(asReassignRow));
                      }}>
                선택 편성변경
              </button>
            )}
            {canEdit && (
              <button className="btn-ghost px-2 py-1 text-xs" disabled={busy || lSend.length === 0}
                      title="고른 항목을 고칩니다 — 하나면 수정 창, 여럿이면 금액·계정명을 한 번에"
                      onClick={() => {
                        if (lSend.length === 1) {
                          const one = lRows.find((r) => Number(r.result_mo_def_id) === lSend[0]);
                          if (one) setDefEdit(one as unknown as Row);
                        } else setBulkEdit([...lSend]);
                      }}>
                선택 수정
              </button>
            )}
            {canEdit && (
              <button className="btn-ghost px-2 py-1 text-xs text-red-600" disabled={busy || lSend.length === 0}
                      title="고른 정기항목을 삭제합니다 — 이미 등록된 달 줄은 남습니다"
                      onClick={() => void removeDefs([...lSend])}>
                선택 삭제
              </button>
            )}
            {xlsBtn('def', nLines(lView))}
          </PaneHead>
          <Pane side="def" groups={lPageGroups} allRows={lRows} loading={left.loading}
                empty={activeFilterCount(lFilters) ? '거르기에 맞는 정기항목이 없습니다.' : endedView ? '편성이 끝난 정기항목이 없습니다.' : '넘길 정기항목이 없습니다.'}
                picked={lPicked} setPicked={setLPicked} selectable={lSel}
                filters={lFilters} setFilters={setLFilters} sort={lSort} setSort={setLSort} resetKey={`${lPage}|${per}|${JSON.stringify(lFilters)}|${JSON.stringify(lSort)}|${q}|${mon}`}
                canEdit={canEdit} onEdit={(r) => setDefEdit(r as unknown as Row)}
                onReassign={(r) => setReassigning([asReassignRow(r)])} onDelete={(r) => void removeDef(r)}
                colOrder={colOrder} setColOrder={setColOrder} colWidths={colWidths} setColWidths={setColWidths} />
          <PanePager owners={lView.length} page={lPage} setPage={setLPage} per={per} setPer={setPer} />
        </section>

        {canEdit && (
          <div className="flex flex-row flex-wrap items-center justify-center gap-2 xl:flex-col">
            <span className="text-center text-[11px] text-ink-faint">왼쪽 {lSend.length.toLocaleString('ko-KR')}건<br />고름</span>
            <button className={mid} disabled={busy || finished || allBlocked} onClick={() => setConfirmAll(true)}
                    title={endedView
                      ? '「편성 종료」 목록에서는 전체등록을 쓰지 않습니다 — 넣을 것만 골라 「선택등록」'
                      : allBlocked
                      ? '검색·거르기·고른 줄이 있으면 전체등록은 꺼집니다 — 「선택등록」을 쓰세요(전부 넣으려면 검색어를 지우고 고름을 푸세요)'
                      : '그 달에 아직 안 넘긴 정기항목 전부'}>전체등록 »</button>
            <button className={`${mid} ${lSend.length > 0 ? 'border-brand-400 font-semibold text-brand-700' : ''}`}
                    disabled={busy || finished || lSend.length === 0} onClick={() => setConfirmPicked(true)}
                    title="왼쪽에서 고른 줄만(지금 걸린 결과 안)">
              {lSend.length > 0 ? `고른 ${lSend.length.toLocaleString('ko-KR')}건 선택등록 ›` : '선택등록 ›'}
            </button>
            {lastBatch && lastBatch.mon === mon && (
              <button className={`${mid} border-amber-400 bg-amber-50 text-amber-800`} disabled={busy || finished}
                      onClick={() => void undoBatch()}
                      title="방금 「전체등록」·「선택등록」으로 넘긴 줄만 되돌립니다(그 전에 등록한 줄은 그대로)">
                ↶ 방금 등록 {lastBatch.ids.length.toLocaleString('ko-KR')}건 되돌리기
              </button>
            )}
            <div className="h-3" />
            <button className={`${mid} text-red-600`} disabled={busy || finished || rSend.length === 0} onClick={() => void cancel(false)}>
              ‹ 선택취소
            </button>
            <button className={`${mid} text-red-600`}
                    disabled={busy || finished || (cancelAllTarget === 'view' && rViewDefIds.length === 0)}
                    onClick={() => void cancelAll()}
                    title={cancelAllTarget === 'all'
                      ? '그 달 정기항목에서 온 줄 전부(수동 등록분은 남김)'
                      : cancelAllTarget === 'picked' ? '오른쪽에서 고른 줄만' : '지금 검색·거르기에 걸린 줄만(수동 등록분은 남김)'}>
              {cancelAllLabel}
            </button>
            <span className="text-center text-[11px] text-ink-faint">오른쪽 {rSend.length.toLocaleString('ko-KR')}건<br />고름</span>
          </div>
        )}

        <section className="card flex min-w-0 flex-col overflow-hidden">
          <PaneHead title={`${mon} 월정산`} data={right.data} view={rView} filtered={activeFilterCount(rFilters) > 0} allRows={rRows}
                    filters={rFilters} setFilters={setRFilters} picked={canEdit ? rSend.length : null}>
            {xlsBtn('month', nLines(rView))}
          </PaneHead>
          <Pane side="month" groups={rPageGroups} allRows={rRows} loading={right.loading}
                empty={activeFilterCount(rFilters) ? '거르기에 맞는 줄이 없습니다.' : '이 달에 등록된 월정기가 없습니다.'}
                picked={rPicked} setPicked={setRPicked} selectable={rSel}
                filters={rFilters} setFilters={setRFilters} sort={rSort} setSort={setRSort} resetKey={`${rPage}|${per}|${JSON.stringify(rFilters)}|${JSON.stringify(rSort)}|${q}|${mon}`}
                canEdit={canEdit} onEdit={(r) => setEditing(r as unknown as Row)} onReassign={() => undefined}
                colOrder={colOrder} setColOrder={setColOrder} colWidths={colWidths} setColWidths={setColWidths} />
          <PanePager owners={rView.length} page={rPage} setPage={setRPage} per={per} setPer={setPer} />
        </section>
      </div>

      {confirmAll && (
        <Modal width="max-w-lg">
          <ApplyAllConfirm
            mon={mon}
            registered={!q && activeFilterCount(rFilters) === 0 ? right.data?.n ?? null : null}
            onClose={() => setConfirmAll(false)}
            onConfirm={() => void apply(true)}
          />
        </Modal>
      )}

      {confirmPicked && (
        <Modal width="max-w-lg">
          <ApplyPickedConfirm
            mon={mon} lines={lSend.length} owners={pickedOwners} q={q}
            onClose={() => setConfirmPicked(false)}
            onConfirm={() => void apply(false)}
          />
        </Modal>
      )}

      {editing && (
        <Modal>
          <MonthEditor
            row={editing}
            driveMon={mon}
            onClose={() => setEditing(null)}
            onSaved={(m) => { setEditing(null); after(m); }}
          />
        </Modal>
      )}

      {defEdit && (
        <Modal>
          <DefForm row={defEdit} onClose={() => setDefEdit(null)} onSaved={(m) => { setDefEdit(null); after(m); }} />
        </Modal>
      )}

      {bulkEdit && (
        <Modal>
          <BulkPanel picked={bulkEdit} noAssign onClose={() => setBulkEdit(null)}
                     onDone={(m) => { setBulkEdit(null); setLPicked(new Set()); after(m); }} />
        </Modal>
      )}

      {reassigning && (
        <Modal>
          <ReassignEditor
            rows={reassigning}
            driveMon={mon}
            finished={finished}
            onClose={() => setReassigning(null)}
            onSaved={(m) => { setReassigning(null); after(m); }}
          />
        </Modal>
      )}
    </div>
  );
}
